import { useEditor } from "@craftjs/core";
import { useMemo } from "react";
import { createPortal } from "react-dom";

import { useBlockSettingsSurfaceOptional } from "@/content-hub/editor/context-menu/block-settings-surface-context";
import { ContextMenuOverlay } from "@/content-hub/editor/context-menu/context-menu-overlay";
import {
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
} from "@/content-hub/store/selectors";
import { hasCustomRecordingDisplayOverrides } from "@/content-hub/theme/primitives/video/recording-metadata-merge";
import {
  displayThumbnailUrlFromUpload,
  RecordingVideoDetailsFields,
} from "@/content-hub/theme/primitives/video/recording-video-details-fields";
import { buildContentHubGridVideoShareUrl } from "@/content-hub/utils/build-content-hub-grid-video-share-url";
import { getCraftNodeDom } from "@/content-hub/utils/get-craft-node-dom";
import { resolveVideoBlockPosterForDetails } from "@/content-hub/utils/resolve-video-block-poster-for-details";
import { useAppSelector } from "@/redux/store";

import type { VideoGridProps } from "../video-grid";

type ListEmbedItem = Extract<
  NonNullable<VideoGridProps["videoList"]>[number],
  { kind: "embed" }
>;

type VideoGridEmbedMetadataPanelProps = {
  open: boolean;
  panelTitle: string;
  onClose: () => void;
  listIndex: number;
  listItem: ListEmbedItem;
  videoGridCraftId: string;
  setProp: (cb: (props: VideoGridProps) => void) => void;
};

export function VideoGridEmbedMetadataPanel(
  props: VideoGridEmbedMetadataPanelProps
) {
  const {
    open,
    panelTitle,
    onClose,
    listIndex,
    listItem,
    videoGridCraftId,
    setProp,
  } = props;

  const surface = useBlockSettingsSurfaceOptional();
  const { query } = useEditor();
  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);

  const resolvedTitle = (listItem.displayTitle ?? listItem.title) || "";

  const localReadonlyShareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return buildContentHubGridVideoShareUrl({
      organizationExternalId,
      pageSlug,
      pageUrl,
      getGridRootElement: () => getCraftNodeDom(query, videoGridCraftId),
      video: {
        id: listItem.id,
        title: resolvedTitle || null,
      },
    });
  }, [
    organizationExternalId,
    pageSlug,
    pageUrl,
    query,
    videoGridCraftId,
    listItem.id,
    resolvedTitle,
  ]);

  if (!open) return null;

  const uploadTxId = `${videoGridCraftId}-${listItem.id}`;

  const patchItem = (patch: Partial<ListEmbedItem>) => {
    setProp((p) => {
      const entry = p.videoList?.[listIndex];
      if (!entry || typeof entry === "string" || entry.kind !== "embed") {
        return;
      }
      Object.assign(entry, patch);
    });
  };

  const panel = (
    <ContextMenuOverlay variant="stack" title={panelTitle} onClose={onClose}>
      <div className="flex flex-col gap-y-4 p-4">
        <RecordingVideoDetailsFields
          analytics={{ kind: "embed", id: listItem.id }}
          localReadonlyShareUrl={localReadonlyShareUrl}
          displayTitle={listItem.displayTitle ?? ""}
          displayDescription={listItem.displayDescription ?? ""}
          displayThumbnail={listItem.displayThumbnail ?? undefined}
          thumbnail={resolveVideoBlockPosterForDetails({
            blockThumbnail: listItem.thumbnail,
            embedUrl: listItem.url,
          })}
          uploadTransactionId={uploadTxId}
          titlePlaceholder={listItem.title}
          onTitleChange={(value) =>
            patchItem({ displayTitle: value || undefined })
          }
          onDescriptionChange={(value) =>
            patchItem({ displayDescription: value || undefined })
          }
          onThumbnailUpload={(imageMetadata) =>
            patchItem({
              displayThumbnail: displayThumbnailUrlFromUpload(imageMetadata),
            })
          }
          showReset={hasCustomRecordingDisplayOverrides(listItem)}
          onReset={() =>
            patchItem({
              displayTitle: undefined,
              displayDescription: undefined,
              displayThumbnail: undefined,
            })
          }
        />
      </div>
    </ContextMenuOverlay>
  );

  const portalTarget = surface?.layoutSurfaceRef.current;
  if (portalTarget) {
    return createPortal(panel, portalTarget);
  }

  return panel;
}
